import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FaEnvelopeOpenText, FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';

const API_URL = (process.env.REACT_APP_SERVER || 'http://localhost:3030').replace(/\/$/, '');

const styles = {
    container: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        fontFamily: 'Arial, sans-serif'
    },
    card: {
        background: '#1e293b',
        borderRadius: '16px',
        padding: '48px',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)'
    },
    icon: {
        fontSize: '64px',
        marginBottom: '24px'
    },
    iconEmail: {
        color: '#8b5cf6'
    },
    iconSuccess: {
        color: '#10b981'
    },
    iconError: {
        color: '#ef4444'
    },
    title: {
        color: '#FFFFFF',
        fontSize: '24px',
        fontWeight: '600',
        marginBottom: '16px'
    },
    subtitle: {
        color: 'rgba(255, 255, 255, 0.6)',
        fontSize: '16px',
        marginBottom: '32px',
        lineHeight: '1.6'
    },
    email: {
        background: 'rgba(139, 92, 246, 0.1)',
        border: '1px solid rgba(139, 92, 246, 0.3)',
        borderRadius: '8px',
        padding: '12px 20px',
        color: '#8b5cf6',
        fontSize: '16px',
        marginBottom: '24px',
        display: 'inline-block'
    },
    btn: {
        padding: '14px 32px',
        borderRadius: '8px',
        border: 'none',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '500',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        transition: 'all 0.2s ease'
    },
    primaryBtn: {
        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        color: '#FFFFFF'
    },
    secondaryBtn: {
        background: 'rgba(100, 116, 139, 0.2)',
        border: '1px solid rgba(100, 116, 139, 0.4)',
        color: '#94A3B8',
        marginLeft: '12px'
    },
    textarea: {
        width: '100%',
        padding: '14px',
        background: 'rgba(0, 0, 0, 0.3)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '8px',
        color: '#FFFFFF',
        fontSize: '14px',
        minHeight: '100px',
        resize: 'vertical',
        marginBottom: '20px',
        fontFamily: 'inherit',
        boxSizing: 'border-box'
    },
    label: {
        display: 'block',
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '14px',
        marginBottom: '8px',
        textAlign: 'left'
    },
    footer: {
        marginTop: '32px',
        paddingTop: '24px',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        color: 'rgba(255, 255, 255, 0.5)',
        fontSize: '13px'
    }
};

const UnsubscribePage = () => {
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState('loading'); // 'loading', 'confirm', 'processing', 'success', 'already', 'error'
    const [expertName, setExpertName] = useState('');
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');

    const email = searchParams.get('email');
    const expertId = searchParams.get('expertId');
    const campaignId = searchParams.get('campaignId');

    useEffect(() => {
        const loadData = async () => {
            if (!email || !expertId) {
                setError('Invalid unsubscribe link. Missing required parameters.');
                setStatus('error');
                return;
            }

            try {
                const response = await axios.get(`${API_URL}/campaigns/unsubscribe`, {
                    params: { email, expertId }
                });

                setExpertName(response.data.expertName || 'this business');
                
                if (response.data.isAlreadyUnsubscribed) {
                    setStatus('already');
                } else {
                    setStatus('confirm');
                }
            } catch (err) {
                console.error('Error loading unsubscribe data:', err);
                setExpertName('this business');
                setStatus('confirm');
            }
        };

        loadData();
    }, [email, expertId]);

    const handleUnsubscribe = async () => {
        setStatus('processing');

        try {
            await axios.post(`${API_URL}/campaigns/unsubscribe`, {
                email,
                expertId,
                campaignId,
                reason
            });

            setStatus('success');
        } catch (err) {
            console.error('Error unsubscribing:', err);
            setError('An error occurred. Please try again later.');
            setStatus('error');
        }
    };

    const renderContent = () => {
        switch (status) {
            case 'loading':
                return (
                    <>
                        <FaSpinner style={{ ...styles.icon, ...styles.iconEmail, animation: 'spin 1s linear infinite' }} />
                        <h1 style={styles.title}>Loading...</h1>
                    </>
                );

            case 'confirm':
                return (
                    <>
                        <FaEnvelopeOpenText style={{ ...styles.icon, ...styles.iconEmail }} />
                        <h1 style={styles.title}>Unsubscribe from Emails</h1>
                        <p style={styles.subtitle}>
                            You're about to unsubscribe from email communications from <strong>{expertName}</strong>.
                        </p>
                        <div style={styles.email}>{email}</div>
                        
                        <div style={{ marginBottom: '24px', textAlign: 'left' }}>
                            <label style={styles.label}>Why are you unsubscribing? (Optional)</label>
                            <textarea
                                style={styles.textarea}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="Your feedback helps us improve..."
                            />
                        </div>

                        <div>
                            <button 
                                style={{ ...styles.btn, ...styles.primaryBtn }}
                                onClick={handleUnsubscribe}
                            >
                                Unsubscribe
                            </button>
                        </div>
                    </>
                );

            case 'processing':
                return (
                    <>
                        <FaSpinner style={{ ...styles.icon, ...styles.iconEmail, animation: 'spin 1s linear infinite' }} />
                        <h1 style={styles.title}>Processing...</h1>
                        <p style={styles.subtitle}>Please wait while we update your preferences.</p>
                    </>
                );

            case 'success':
                return (
                    <>
                        <FaCheckCircle style={{ ...styles.icon, ...styles.iconSuccess }} />
                        <h1 style={styles.title}>Successfully Unsubscribed</h1>
                        <p style={styles.subtitle}>
                            You will no longer receive marketing emails from <strong>{expertName}</strong>.
                        </p>
                        <div style={styles.email}>{email}</div>
                        <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '14px', marginTop: '24px' }}>
                            You may still receive important transactional emails like appointment confirmations.
                        </p>
                    </>
                );

            case 'already':
                return (
                    <>
                        <FaCheckCircle style={{ ...styles.icon, ...styles.iconSuccess }} />
                        <h1 style={styles.title}>Already Unsubscribed</h1>
                        <p style={styles.subtitle}>
                            This email address is already unsubscribed from <strong>{expertName}</strong>'s mailing list.
                        </p>
                        <div style={styles.email}>{email}</div>
                    </>
                );

            case 'error':
                return (
                    <>
                        <FaTimesCircle style={{ ...styles.icon, ...styles.iconError }} />
                        <h1 style={styles.title}>Something Went Wrong</h1>
                        <p style={styles.subtitle}>{error}</p>
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                {renderContent()}
                <div style={styles.footer}>
                    Powered by Maven AI
                </div>
            </div>

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default UnsubscribePage;
